import { useState, useMemo } from 'react';
import type { OptionTypeBase, ValueType } from 'react-select';

import {
  Badge,
  Box,
  Flex,
  Select,
  type PortalProps,
} from '@frontend/legacy-components';

import { useTemplateQuery } from './data/hooks';

const IS_MULTI = false;

type IsMulti = typeof IS_MULTI;

interface OptionType extends OptionTypeBase {
  label: string;
  value: number;
}

interface IntegrationTemplateSelectorProps {
  onChange: (
    e: { templateId: number; name: string } | 'NO_TEMPLATE' | null,
  ) => void;
  menuPortalDestinationRef?: PortalProps['containerRef'];
}

const IntegrationTemplateSelector = ({
  onChange,
  menuPortalDestinationRef,
}: IntegrationTemplateSelectorProps) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<ValueType<OptionType, IsMulti>>(null);

  const {
    data: templateList,
    isFetching,
    isError,
  } = useTemplateQuery({ pageSize: 30 });

  const options = useMemo(() => {
    const templateOptions = templateList?.results
      ? templateList.results.map((template) => ({
          label: template.isIccCompatible ? (
            <Flex direction="row" align="center" gap={1}>
              {template.name}
              <Badge bg="status.notStarted" borderColor="status.notStarted">
                Can Use Cloud Connector
              </Badge>
            </Flex>
          ) : (
            template.name
          ),
          value: template.id,
        }))
      : [];
    templateOptions.push({
      label: 'Custom integration (no template)',
      value: -1,
    });
    return templateOptions;
  }, [templateList]);

  const doChange = (e: ValueType<OptionType, IsMulti>): void => {
    setSelectedTemplate(e);
    if (!e) {
      onChange(null);
    } else if (e.value === -1) {
      onChange('NO_TEMPLATE');
    } else {
      const t = templateList?.results?.find(
        (template) => template.id === e.value,
      );
      onChange(
        t
          ? {
              templateId: e.value,
              name: t.name,
            }
          : null,
      );
    }
  };

  return (
    <Box w="100%">
      <Select
        isMulti={IS_MULTI}
        isLoading={isFetching}
        isDisabled={isError}
        isClearable={true}
        placeholder="Select a template"
        value={selectedTemplate}
        options={options}
        onChange={doChange}
        menuPortalDestinationRef={menuPortalDestinationRef}
      />
    </Box>
  );
};

export default IntegrationTemplateSelector;
